import React from 'react'
import { Control } from 'react-redux-form'
import styled, { css } from 'styled-components'
import { PlaceholderStyle } from 'components/form-components/styled-text-input'
import { ThemeSpace } from 'theme/spaces'
import { useStore } from 'react-redux'

export interface StyledTextAreaProps {
  model?: string
  type?: string
  autoComplete?: string
  children?: React.ReactNode | React.ReactNode[]
  className?: string
  content?: string
  maxLength?: number
  placeholder?: string
  readOnly?: boolean
  onChange?: any
  updateOn?: any
  validateOn?: any
  validators?: any
  rows?: number
  maxWidth?: ThemeSpace
  value?: string
}

export const TextAreaStyle = css<{
  $maxWidth?: ThemeSpace
  $readOnly?: boolean
}>`
  background-color: ${({ theme }) => theme.color.grey07};
  border: none;
  color: ${({ theme, $readOnly }) =>
    $readOnly ? theme.color.grey90 : theme.color.bodyText};
  height: auto;
  min-height: ${({ theme }) => theme.space.s40};
  line-height: ${({ theme }) => theme.space.s24};
  width: ${({ $maxWidth, theme }) =>
    $maxWidth ? theme.space[$maxWidth] : '100%'};
  max-width: ${({ $maxWidth, theme }) =>
    $maxWidth ? theme.space[$maxWidth] : '100%'};
  padding: ${({ theme }) => theme.space.s8} ${({ theme }) => theme.space.s16};
  outline: none;
  overflow: auto;

  cursor: ${({ $readOnly }) => ($readOnly ? 'not-allowed' : 'auto')};

  &:disabled {
    background-color: ${({ theme }) => theme.color.grey05};
    color: ${({ theme }) => theme.color.grey30};
    cursor: not-allowed;
  }

  /* Chrome, Firefox, Opera, Safari 10.1+ */
  ::placeholder {
    ${PlaceholderStyle};
  }

  /* Microsoft Edge */
  ::-ms-input-placeholder {
    ${PlaceholderStyle};
  }
`

interface TextAreaControlFieldProps
  extends React.ComponentProps<typeof Control.textarea> {
  $maxWidth?: ThemeSpace
}

const TextAreaControlField = styled(Control.textarea).withConfig({
  shouldForwardProp: prop => prop !== '$maxWidth',
})<TextAreaControlFieldProps>`
  ${TextAreaStyle};
`

const TextAreaField = styled.textarea<{ $maxWidth?: ThemeSpace }>`
  ${TextAreaStyle};
`

export const StyledTextArea = ({
  model,
  type = 'text',
  autoComplete,
  children,
  className,
  content,
  maxLength,
  placeholder,
  readOnly = false,
  onChange,
  updateOn = 'change',
  validateOn = 'change',
  validators,
  maxWidth,
  rows,
  value = '',
  ...props
}: StyledTextAreaProps) => {
  if (model) {
    const store = useStore()
    return (
      <TextAreaControlField
        {...props}
        // @ts-ignore
        store={store}
        model={model}
        type={type}
        autoComplete={autoComplete}
        className={className}
        content={content}
        maxLength={maxLength}
        placeholder={placeholder}
        readOnly={readOnly}
        $readOnly={readOnly}
        onChange={onChange}
        aria-label={model}
        updateOn={updateOn}
        validateOn={validateOn}
        validators={validators}
        rows={rows}
        $maxWidth={maxWidth}
      />
    )
  }
  return (
    <>
      <TextAreaField
        {...props}
        value={value}
        autoComplete={autoComplete}
        className={className}
        maxLength={maxLength}
        aria-label={model}
        placeholder={placeholder}
        readOnly={readOnly}
        onChange={onChange}
        rows={rows}
        $maxWidth={maxWidth}
      />
      {children}
    </>
  )
}
